/**
 * Deliverables view helpers — group version tracks by proposal structure and
 * summarize where each track's head version has gone (client portal / suppliers).
 * Pure functions; the Files tab and tests share them.
 */

import type {
  PortalFileRelease,
  ProjectFile,
  ProjectFileStream,
  RfqHubFileRelease,
  VendorFileRelease,
} from '../types';
import type { StructureRef } from './proposalStructureRefs';
import { PROJECT_FILE_DETAIL_STAGE_LABELS, type ProjectFileDetailStage } from './projectFileVersionGroups';
import { structureSelectionLabel, structureSelectionTitle, structureSetKey } from './structureSelectionLabel';
import { compareStreamsByRailOrder } from './projectFileStructureTracks';
import { headVersionForStream } from './projectFileStreamVersions';

export const WHOLE_PROJECT_GROUP_ID = '__whole_project__';
export const WHOLE_PROJECT_GROUP_LABEL = 'Whole project';

export interface StreamStructureGroup {
  /** Structure-set key (`structureSetKey`), or `WHOLE_PROJECT_GROUP_ID`. */
  structureId: string;
  /** "Barn" / "Barn + Pavilion" / "Barn + Pavilion +1" / "Whole project". */
  structureName: string;
  /** Full comma list for tooltips when the set has several structures. */
  structureTitle?: string;
  /** User-chosen group name (`ProjectFileStream.structureGroupLabel`), when the group was renamed. */
  customLabel?: string;
  structureIds: string[];
  structureNames: string[];
  streams: ProjectFileStream[];
}

/**
 * Group tracks by the structure *set* they are tagged with — the unit the split
 * modal creates — so "Lower Canopy + Main Porch +1" is one section holding all
 * four of its tracks, not four sections each repeating them. Sets are ordered by
 * where their first structure sits in the proposal (unknown tags after), and
 * untagged tracks go under "Whole project" last.
 */
export function groupStreamsByStructure(
  streams: ProjectFileStream[],
  structures: StructureRef[],
): StreamStructureGroup[] {
  const names = new Map<string, string>(structures.map(s => [s.id, s.name]));
  for (const s of streams) {
    (s.structureIds ?? []).forEach((id, i) => {
      if (!names.has(id) && s.structureNames?.[i]) names.set(id, s.structureNames[i]);
    });
  }
  const proposalOrder = new Map<string, number>(structures.map((s, i) => [s.id, i]));
  const rank = (id: string) => proposalOrder.get(id) ?? Number.MAX_SAFE_INTEGER;

  const buckets = new Map<string, { ids: string[]; streams: ProjectFileStream[] }>();
  for (const s of streams) {
    const ids = Array.from(new Set((s.structureIds ?? []).filter(Boolean))).sort((a, b) => rank(a) - rank(b));
    const key = ids.length === 0 ? WHOLE_PROJECT_GROUP_ID : structureSetKey(ids);
    const bucket = buckets.get(key) ?? { ids, streams: [] };
    bucket.streams.push(s);
    buckets.set(key, bucket);
  }

  const keys = Array.from(buckets.keys()).filter(k => k !== WHOLE_PROJECT_GROUP_ID);
  keys.sort((a, b) => {
    const ia = buckets.get(a)!.ids;
    const ib = buckets.get(b)!.ids;
    for (let i = 0; i < Math.max(ia.length, ib.length); i += 1) {
      // A set that runs out first sorts first ("Barn" before "Barn + Pavilion").
      const ra = ia[i] === undefined ? -1 : rank(ia[i]);
      const rb = ib[i] === undefined ? -1 : rank(ib[i]);
      if (ra !== rb) return ra - rb;
    }
    return 0; // stable: first-seen order for sets the proposal does not know
  });
  if (buckets.has(WHOLE_PROJECT_GROUP_ID)) keys.push(WHOLE_PROJECT_GROUP_ID);

  return keys.map(key => {
    const bucket = buckets.get(key)!;
    if (key === WHOLE_PROJECT_GROUP_ID) {
      return {
        structureId: key,
        structureName: WHOLE_PROJECT_GROUP_LABEL,
        structureIds: [],
        structureNames: [],
        streams: [...bucket.streams].sort(compareStreamsByRailOrder),
      };
    }
    const setNames = bucket.ids.map(id => names.get(id) || 'Structure');
    const customLabel = bucket.streams.map(s => (s.structureGroupLabel || '').trim()).find(Boolean);
    const autoLabel = structureSelectionLabel(setNames, { maxNamed: 3 });
    return {
      structureId: key,
      structureName: customLabel || autoLabel,
      structureTitle: customLabel ? `${customLabel} — ${structureSelectionTitle(setNames)}` : structureSelectionTitle(setNames),
      customLabel: customLabel || undefined,
      structureIds: bucket.ids,
      structureNames: setNames,
      streams: [...bucket.streams].sort(compareStreamsByRailOrder),
    };
  });
}

function firestoreDate(value: unknown): Date | undefined {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const toDate = (value as { toDate?: () => Date } | null)?.toDate;
  if (typeof toDate !== 'function') return undefined;
  const date = toDate.call(value);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : undefined;
}

/**
 * Stream fields the approval trail needs. Includes when a structure track was
 * added, so a group created after a whole-project sign-off does not inherit it.
 */
export function approvalTrailStream(id: string, data: Record<string, unknown>): ProjectFileStream {
  const createdAt = firestoreDate(data.createdAt) ?? new Date(0);
  return {
    id,
    projectId: typeof data.projectId === 'string' ? data.projectId : '',
    title: typeof data.title === 'string' ? data.title : '',
    streamKind: (data.streamKind as ProjectFileStream['streamKind']) || 'general',
    createdAt,
    createdBy: '',
    updatedAt: createdAt,
    structureIds: Array.isArray(data.structureIds) ? (data.structureIds as string[]) : [],
    structureNames: Array.isArray(data.structureNames) ? (data.structureNames as string[]) : [],
    structureGroupLabel: typeof data.structureGroupLabel === 'string' ? data.structureGroupLabel : undefined,
    forkedFromStreamId:
      typeof data.forkedFromStreamId === 'string' && data.forkedFromStreamId ? data.forkedFromStreamId : undefined,
    inheritVersionsAfter: firestoreDate(data.inheritVersionsAfter),
  };
}

/** Files-tab groups the approval trail can draw as one row. */
export interface ApprovalTrailStructureGroup {
  label: string;
  structureIds: string[];
  structureNames: string[];
}

/**
 * Matthew (t787u): grouped/split design-approval view only when the project has
 * at least this many separately *named* structure groups (`structureGroupLabel`).
 * Multiple structures alone are not enough — keep in sync with
 * functions/src/portalApprovalTrailGrouping.ts.
 */
export const MIN_NAMED_STRUCTURE_GROUPS_FOR_TRAIL = 2;

/**
 * Groups worth a single approval row: a renamed group, or a set of more than
 * one structure — but only when there are ≥ {@link MIN_NAMED_STRUCTURE_GROUPS_FOR_TRAIL}
 * named groups on the project. Otherwise return [] so staff Client view and
 * the portal both stay on the ungrouped / Whole-project path.
 */
export function approvalTrailStructureGroups(
  streams: ProjectFileStream[],
  structures: StructureRef[],
): ApprovalTrailStructureGroup[] {
  const candidates = groupStreamsByStructure(streams, structures)
    .filter(group => group.structureId !== WHOLE_PROJECT_GROUP_ID && group.structureIds.length > 0)
    .filter(group => !!group.customLabel || group.structureIds.length > 1);
  const namedCount = candidates.filter(group => !!group.customLabel).length;
  if (namedCount < MIN_NAMED_STRUCTURE_GROUPS_FOR_TRAIL) return [];
  return candidates.map(group => ({
    label: group.structureName,
    structureIds: group.structureIds,
    structureNames: group.structureNames,
  }));
}

/** A Files-rail structure group the upload modal can target as one choice. */
export interface UploadStructureGroup {
  id: string;
  label: string;
  structureIds: string[];
  /** Member names, for the hover title. */
  title?: string;
  /** Renamed rail heading. A new track of this group keeps the name. */
  customLabel?: string;
}

/**
 * Structure groups already on the Files rail. Upload picks one of these,
 * not each proposal structure inside it. Whole project stays a separate choice.
 */
export function uploadStructureGroups(
  streams: ProjectFileStream[],
  structures: StructureRef[],
): UploadStructureGroup[] {
  return groupStreamsByStructure(streams, structures)
    .filter(group => group.structureId !== WHOLE_PROJECT_GROUP_ID && group.structureIds.length > 0)
    .map(group => ({
      id: group.structureId,
      label: group.structureName,
      structureIds: group.structureIds,
      title: group.structureNames.length > 1 ? group.structureNames.join(', ') : undefined,
      customLabel: group.customLabel,
    }));
}

export function structureGroupIsSelected(
  structureIds: readonly string[],
  selected: ReadonlySet<string>,
): boolean {
  return structureIds.length > 0 && structureIds.every(id => selected.has(id));
}

/** Add or remove every structure in a group together. */
export function toggleUploadStructureGroup(
  value: readonly string[],
  structureIds: readonly string[],
): string[] {
  const selected = new Set(value);
  if (structureGroupIsSelected(structureIds, selected)) {
    const drop = new Set(structureIds);
    return value.filter(id => !drop.has(id));
  }
  const nextIds = value.filter(id => !structureIds.includes(id));
  structureIds.forEach(id => {
    if (!value.includes(id)) nextIds.push(id);
  });
  return nextIds;
}

export function selectedUploadStructureGroups<T extends { structureIds: readonly string[] }>(
  value: readonly string[],
  groups: readonly T[],
): T[] {
  const selected = new Set(value);
  return groups.filter(group => structureGroupIsSelected(group.structureIds, selected));
}

/** Which rail group a track belongs to (`structureSetKey` of its tags, or Whole project). */
export function groupKeyForStream(stream: Pick<ProjectFileStream, 'structureIds'>): string {
  const ids = (stream.structureIds ?? []).filter(Boolean);
  return ids.length === 0 ? WHOLE_PROJECT_GROUP_ID : structureSetKey(ids);
}

const STREAM_TITLE_SEPARATORS = [' — ', ' - ', ' – '] as const;

/**
 * Prefix strings a split may have written from these structure names, longest first.
 * Individual names are included so "Barn — Drawings" still updates when Barn is renamed,
 * even inside a combined group. Display stripping leaves them out so a track that merely
 * starts with one member's name is not shortened.
 */
export function structureNamePrefixLabels(
  names: ReadonlyArray<string> | null | undefined,
  opts?: { includeIndividuals?: boolean },
): string[] {
  const clean = (names ?? []).map(n => (n || '').trim()).filter(Boolean);
  if (!clean.length) return [];
  const seen = new Set<string>();
  const labels: string[] = [];
  const forms = [
    structureSelectionLabel(clean, { maxNamed: 3 }),
    structureSelectionLabel(clean, { maxNamed: 2 }),
    clean.join(' + '),
    ...(opts?.includeIndividuals === false ? [] : clean),
  ];
  for (const label of forms) {
    const t = label.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    labels.push(t);
  }
  labels.sort((a, b) => b.length - a.length);
  return labels;
}

/**
 * Swap a known structure prefix on a stored track title.
 * `newLabel` empty strips the prefix (track left its group). Returns null when nothing should be written.
 */
export function replaceStreamTitlePrefix(
  title: string,
  oldLabels: readonly string[],
  newLabel: string,
): string | null {
  const current = (title || '').trim();
  if (!current) return null;
  const next = (newLabel || '').trim();
  const labels = oldLabels.map(l => l.trim()).filter(Boolean).sort((a, b) => b.length - a.length);
  for (const old of labels) {
    for (const sep of STREAM_TITLE_SEPARATORS) {
      const prefix = `${old}${sep}`;
      if (!current.startsWith(prefix)) continue;
      const rest = current.slice(prefix.length).trim();
      if (!rest) continue;
      if (!next) return rest === current ? null : rest;
      if (old === next) return null;
      const updated = `${next}${sep}${rest}`;
      return updated === current ? null : updated;
    }
  }
  return null;
}

/**
 * Keep the stored structure prefix when someone edits the name shown in the group.
 * `editedDisplay` is that visible name, without the prefix.
 */
export function titleWithPreservedPrefix(
  title: string,
  labels: readonly string[],
  editedDisplay: string,
): string {
  const edited = editedDisplay.trim();
  const current = (title || '').trim();
  const ordered = labels.map(l => l.trim()).filter(Boolean).sort((a, b) => b.length - a.length);
  for (const label of ordered) {
    for (const sep of STREAM_TITLE_SEPARATORS) {
      const prefix = `${label}${sep}`;
      if (current.startsWith(prefix) && current.slice(prefix.length).trim()) {
        return `${label}${sep}${edited}`;
      }
    }
  }
  return edited;
}

function titleWords(value: string): string[] {
  return value.toLowerCase().split(/\s+/).filter(Boolean);
}

/**
 * The group was renamed shorter than the prefix the split wrote
 * ("Structural Oak" vs "Structural White Oak — 2Ds"). The group words still
 * appear, in order, inside that longer prefix.
 */
function groupLabelFitsLongerPrefix(groupLabel: string, prefix: string): boolean {
  const group = titleWords(groupLabel);
  const lead = titleWords(prefix);
  if (group.length < 2 || lead.length <= group.length || group[0] !== lead[0]) return false;
  let index = 0;
  for (const word of lead) {
    if (word === group[index]) index += 1;
    if (index === group.length) return true;
  }
  return false;
}

function stripLongerGroupPrefix(title: string, label: string): string | null {
  const group = label.trim();
  if (!group) return null;
  for (const sep of STREAM_TITLE_SEPARATORS) {
    const at = title.indexOf(sep);
    if (at <= 0) continue;
    const prefix = title.slice(0, at).trim();
    const rest = title.slice(at + sep.length).trim();
    if (rest && groupLabelFitsLongerPrefix(group, prefix)) return rest;
  }
  return null;
}

/**
 * Title to show for a track *inside its own group*. The split stores
 * "{group label} — {original}" so sibling tracks sort together elsewhere; within
 * the group section that prefix is noise, so strip it. A renamed group still
 * strips a longer prefix it was shortened from. Falls back to the stored
 * title when it doesn't start with the group label (user-renamed tracks).
 */
export function streamDisplayTitle(
  stream: Pick<ProjectFileStream, 'title' | 'structureNames'> & Partial<Pick<ProjectFileStream, 'structureGroupLabel'>>,
  groupLabel?: string | null,
): string {
  const title = (stream.title || '').trim();
  const candidates = new Set<string>();
  if (groupLabel) candidates.add(groupLabel.trim());
  if ('structureGroupLabel' in stream && stream.structureGroupLabel) candidates.add(stream.structureGroupLabel.trim());
  for (const label of structureNamePrefixLabels(stream.structureNames, { includeIndividuals: false })) candidates.add(label);
  for (const c of Array.from(candidates)) {
    if (!c) continue;
    for (const sep of STREAM_TITLE_SEPARATORS) {
      const prefix = `${c}${sep}`;
      if (title.startsWith(prefix) && title.length > prefix.length) return title.slice(prefix.length).trim();
    }
  }
  for (const label of Array.from(candidates)) {
    const stripped = stripLongerGroupPrefix(title, label);
    if (stripped) return stripped;
  }
  return title;
}

/** True when any track on the project is tagged — drives whether the structure rail shows. */
export function anyStreamTagged(
  streams: ReadonlyArray<Pick<ProjectFileStream, 'structureIds'>> | null | undefined,
): boolean {
  return (streams ?? []).some(s => (s.structureIds ?? []).length > 0);
}

export interface StreamStatusSummary {
  /** LOD of the head (newest) version, when known. */
  headLod: ProjectFileDetailStage | null;
  headLodLabel: string | null;
  /** Number of versions in this track that have ever been released to the client portal. */
  releasedVersions: number;
  /** Head version is the one currently on the client portal. */
  headReleased: boolean;
  /** Head version has a newer un-released revision (released version exists but isn't head). */
  headAheadOfPortal: boolean;
  /** Number of versions staged for suppliers (hub releases). */
  stagedVersions: number;
  headStaged: boolean;
  /** Number of versions sent to CNC vendors. */
  vendorVersions: number;
}

export interface SummarizeStreamStatusInput {
  stream: ProjectFileStream;
  /** All project files (head lookup) — may be the full project list. */
  files: ProjectFile[];
  portalReleases: PortalFileRelease[];
  hubReleases: RfqHubFileRelease[];
  vendorReleases: VendorFileRelease[];
}

export function summarizeStreamStatus(input: SummarizeStreamStatusInput): StreamStatusSummary {
  const { stream } = input;
  const sid = stream.id ?? '';
  const head = headVersionForStream(stream, input.files);
  const headId = head?.id ?? '';

  // A structure fork inherits its root's pre-split versions — and their releases.
  // Count only releases of versions that are actually in this track's history, so a
  // root version later moved onto a sibling fork stops counting here.
  const ids = new Set([sid, stream.forkedFromStreamId || ''].filter(Boolean));
  const versionIds = new Set(input.files.filter(f => f.streamId && ids.has(f.streamId)).map(f => f.id));
  const inTrack = (r: { sourceStreamId: string; sourceFileId: string }) =>
    ids.has(r.sourceStreamId) && (versionIds.size === 0 || versionIds.has(r.sourceFileId));
  const portal = input.portalReleases.filter(inTrack);
  const hub = input.hubReleases.filter(inTrack);
  const vendor = input.vendorReleases.filter(inTrack);

  const releasedFileIds = new Set(portal.map(r => r.sourceFileId));
  const stagedFileIds = new Set(hub.map(r => r.sourceFileId));

  const headReleased = !!headId && releasedFileIds.has(headId);
  const headLod = head?.detailStage ?? null;
  return {
    headLod,
    headLodLabel: headLod ? PROJECT_FILE_DETAIL_STAGE_LABELS[headLod] : null,
    releasedVersions: releasedFileIds.size,
    headReleased,
    headAheadOfPortal: releasedFileIds.size > 0 && !headReleased,
    stagedVersions: stagedFileIds.size,
    headStaged: !!headId && stagedFileIds.has(headId),
    vendorVersions: new Set(vendor.map(r => r.sourceFileId)).size,
  };
}

/** Short chip text for a structure tag on a track row. */
export function streamStructureChipLabel(stream: ProjectFileStream): string | null {
  const names = (stream.structureNames ?? []).filter(Boolean);
  if (names.length === 0) return (stream.structureIds ?? []).length > 0 ? 'Tagged' : null;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} + ${names[1]}`;
  return `${names[0]} +${names.length - 1}`;
}
